import { useMemo } from 'react';

import { SidebarGroup, SidebarGroupContent } from '@/components/ui/sidebar';
import { SortableTree } from '@/components/ui/sortable-tree';
import { MutedText } from '@/components/ui/typography';
import type { Page } from '@/types/page';
import { PageTreeSkeleton } from './index';
import { filterPages, pagesToTreeItems, type ReorderOperation } from './page-utils';
import { PageVisibilityWarningDialog } from './page-visibility-warning';
import { SortablePageItem } from './sortable-page-item';
import { usePageReorderWarning } from './use-page-reorder-warning';

interface SortablePageTreeProps {
  pages: Page[];
  isLoading: boolean;
  searchQuery: string;
  emptyStateMessages: {
    noResults: string;
    noPages: string;
  };
  onPagesReorder?: (operation: ReorderOperation, nextTree: Page[]) => void | Promise<void>;
  onOpenNestedView?: (page: Page) => void;
}

export function SortablePageTree({
  pages,
  isLoading,
  searchQuery,
  emptyStateMessages,
  onPagesReorder,
  onOpenNestedView,
}: SortablePageTreeProps) {
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredPages = useMemo(
    () => filterPages(pages, normalizedQuery),
    [pages, normalizedQuery],
  );

  const isFiltering = normalizedQuery.length > 0;

  // Convert pages to tree items format
  const treeItems = useMemo(
    () => pagesToTreeItems(filteredPages, { onOpenNestedView }),
    [filteredPages, onOpenNestedView],
  );
  const {
    confirmPendingReorder,
    handleItemsChanged,
    localTreeItems,
    pendingReorderWarning,
    setPendingReorderWarning,
  } = usePageReorderWarning({
    treeItems,
    onPagesReorder,
  });

  if (isLoading && !isFiltering) {
    return <PageTreeSkeleton />;
  }

  const isEmpty = filteredPages.length === 0;

  if (isEmpty) {
    return (
      <SidebarGroup className="p-0">
        <SidebarGroupContent>
          <div className="px-2 py-2">
            <div className="rounded-lg border border-dashed border-sidebar-border p-8 text-center">
              <MutedText className="text-xs">
                {isFiltering
                  ? isLoading
                    ? 'Searching...'
                    : emptyStateMessages.noResults
                  : emptyStateMessages.noPages}
              </MutedText>
            </div>
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <>
      <SidebarGroup className="p-0">
        <SidebarGroupContent>
          <SortableTree
            items={localTreeItems}
            onItemsChanged={handleItemsChanged}
            TreeItemComponent={SortablePageItem}
            dropAnimation={null}
            sortableProps={{ animateLayoutChanges: () => false, disabled: isFiltering }}
          />
        </SidebarGroupContent>
      </SidebarGroup>
      <PageVisibilityWarningDialog
        open={pendingReorderWarning !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingReorderWarning(null);
          }
        }}
        variant="move"
        onConfirm={confirmPendingReorder}
        onCancel={() => setPendingReorderWarning(null)}
      />
    </>
  );
}
